import { AcademicCapIcon, ClockIcon, GiftIcon, HeartIcon } from '@heroicons/react/24/outline';

import mathCapabilitiesArt from '../assets/math-capabilities.webp';
import { CHROME_EXTENSION_URL } from '../constants/chromeExtension';
import { SEPTEMBER_SCRAMBLE_PROGRAM_KEY } from '../constants/septemberScramble';
import { INVITE_SOURCE } from '../constants/linkSources';
import {
  getChurnState,
  isPremiumExpiringSoon,
  isTrialExpiringSoon,
  isTrialRecentlyEnded,
} from '../utils/churnedOrg';
import { AD_IDS, EXPORT_AD_IDS, SIDEBAR_AD_IDS } from './adKeys';
import { isDonutsAudience, isMathCapable, isMathStarTeacher, isOrgAdmin } from './adPredicates';
import {
  ADMIN_FIRST_SEEN_KEY,
  CURRICULUM_LEARN_MORE_URL,
  TEACHER_FIRST_SEEN_KEY,
  adminShareMailto,
  adminShareMessage,
  curriculumLessonHref,
  curriculumName,
  isWithinAnnouncementWindow,
  teachesCurriculumGrades,
} from './curriculumAnnouncement';
import { isDonutsHostEligible } from './donutsAdEligibility';
import { shareReferralModal } from './shareReferralModal';
import { isExtensionInstalled } from '../services/ExtensionBridge';
import { mathShareModal } from './mathShareModal';

export const AD_REFERRAL_PROGRAM_KEY = SEPTEMBER_SCRAMBLE_PROGRAM_KEY;

const isCurriculumAnnouncementTeacher = (ctx) =>
  !!ctx.announcedCurriculumId &&
  !isOrgAdmin(ctx.diffitUser) &&
  ctx.tier !== 'free' &&
  isMathCapable(ctx.diffitUser) &&
  teachesCurriculumGrades(ctx.diffitUser, ctx.announcedCurriculumGrades) &&
  isWithinAnnouncementWindow(ctx.diffitUser?.seen_nux_json, TEACHER_FIRST_SEEN_KEY);

const DoughnutIcon = ({ className }) => (
  <span className={`${className} text-2xl leading-none`} aria-hidden='true'>
    🍩
  </span>
);

const MATH_PROMPT = 'A math mini lesson with guided notes, practice problems, and exit ticket for ';

export const MATH_PROMPTS = [
  {
    emoji: '📊',
    label: 'Graphs & data analysis',
    prompt: 'A math lesson with graphs and data analysis for ',
  },
  {
    emoji: '∑',
    label: 'Equations & worked solutions',
    prompt: 'A math practice set with multi-step equations and worked solutions for ',
  },
  {
    emoji: '📐',
    label: 'Diagrams & visual models',
    prompt: 'A math lesson with labeled diagrams and visual models for ',
  },
];

const RENEW_CTA = 'Ask My Admin to Renew';

// The renewal ask, as an ad descriptor's `modal`. Every surface opens the SAME modal, which
// words itself from the teacher's own subscription state (shareWithAdminCopy) — so there is no
// copy to pass, only the location the modal stamps on its own `Share With Admin Sent`.
const renewModal = (location) => ({ key: 'shareWithAdmin', props: { location } });

// The ask a school too small for the admin route earns instead: invite colleagues, and the
// referral program pays both sides. The modal already words the offer (and names a churned
// school when there is one), so there is nothing to pass but the location.
const shareForPremiumModal = (location) => ({ key: 'shareForPremium', props: { location } });

// The offer needs two or more colleagues to sign up, which the modal spells out. The cards say
// "share Diffit with colleagues" rather than naming a number they would then have to keep in
// step with the program's configuration.
const INVITE_CTA = 'Get 30 more days';

// A school that never subscribed has nothing to renew, so its admin is asked to buy.
const BUY_CTA = 'Share Diffit';

const DONUTS_COPY = {
  headline: 'Host a Diffit PD',
  body: "We'll provide the snacks and materials!",
};

const MATH_CHALLENGE_COPY = {
  headline: "You've made great resources!",
  body: 'Get rewards for inspiring other math teachers.',
};

const MATH_SHARE_COPY = {
  headline: 'Share the love',
  body: 'Send a free year of Diffit Premium to fellow math teachers.',
};

const MATH_FREE_YEAR_COPY = {
  headline: 'Try Diffit for Math, get a free year of Premium',
  body: 'Now with improved graphs, charts & diagrams. Redeem inside a math resource.',
};

const REFERRAL_COPY = {
  headline: 'Love Diffit? Share Diffit',
  body: 'Earn rewards for every teacher you bring to Diffit.',
};

/**
 * Campaigns in priority order. See the header for how a surface entry overrides.
 */
const CAMPAIGNS = [
  {
    // Above every promotional campaign, and both expiry rungs above those: a teacher about to
    // lose the product has a more urgent thing to read than a referral offer.
    //
    // `{days}` is filled per teacher at render (adCopyTokens) — the registry is built once at
    // module scope, so the number cannot be baked in here.
    //
    // Targets `canShareWithAdmin`, not "is expiring": the card's only CTA opens the
    // share-with-admin modal, and POST /user/share-with-admin is hard-gated on the
    // paygate-premium-offer flag and on a school sender domain. Where that ask is a dead end
    // this card stays down rather than opening a modal whose every submit 400s.
    //
    // Deliberately NOT gated on the school earning the admin ask, unlike every other card
    // here. The others hand a small school the invite route instead; this one has no invite
    // twin to hand them — the referral reward is the same 30 days, and a teacher still inside
    // their licence cannot be granted them (see _async_apply_reward's is_paid skip). So the
    // renewal ask goes to every school, whatever its active-teacher count.
    name: 'premiumExpiring',
    ctaLabel: RENEW_CTA,
    eligible: (ctx) =>
      ctx.canShareWithAdmin && isPremiumExpiringSoon(ctx.diffitUser, ctx.expiringSoonWindowDays),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.PREMIUM_EXPIRING,
        emoji: '⏳',
        headline: 'Your premium ends {days}',
        // No thank-you promised here, unlike the lapsed card below: the extension is a promo
        // code, and /promo/redeem refuses to spend one on a user who is still paid — which
        // this teacher is until the date actually passes.
        body: "Ask your admin to renew — we'll keep your resources right where they are.",
        modal: renewModal('sidebar_premium_expiring_ad'),
      },
      resource: {
        id: AD_IDS.PREMIUM_EXPIRING,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your premium ends {days}.',
        body: 'Ask your admin to renew so you keep premium.',
        modal: renewModal('resource_premium_expiring_ad'),
      },
    },
  },
  {
    // The lapsed half of the same campaign, and a separate ad id: dismissing the warning must
    // not also dismiss the notice that the thing warned about has happened.
    //
    // `offersExtensionDays` is what makes the 30 days safe to promise — it is false once the
    // teacher has spent the code, so the copy never offers it twice. `tier !== 'paid'` keeps
    // the card off a teacher whose school lapsed but who kept premium another way (an
    // individual plan, a premium domain, an ambassador badge).
    name: 'premiumEnded',
    ctaLabel: RENEW_CTA,
    eligible: (ctx) =>
      ctx.canAskAdminToRenew &&
      ctx.offersExtensionDays &&
      ctx.tier !== 'paid' &&
      getChurnState(ctx.diffitUser).state === 'recent',
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.PREMIUM_ENDED,
        emoji: '⏳',
        headline: 'Your school subscription has ended',
        body: "Ask your admin to renew — we'll add 30 days while they process the renewal.",
        modal: renewModal('sidebar_premium_ended_ad'),
      },
      resource: {
        id: AD_IDS.PREMIUM_ENDED,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your school subscription has ended.',
        body: 'Get 30 more days of premium when you ask your admin to renew.',
        modal: renewModal('resource_premium_ended_ad'),
      },
    },
  },
  {
    // premiumEnded for a school too small to have an admin holding a budget. Same state, same
    // 30 days, different route to them — so it is a separate campaign rather than a branch:
    // the CTA, the modal and the dismissal all differ, and `ask` is what picks between them.
    name: 'premiumEndedInvite',
    ctaLabel: INVITE_CTA,
    eligible: (ctx) =>
      ctx.canAskColleaguesToShare &&
      ctx.offersExtensionDays &&
      ctx.tier !== 'paid' &&
      getChurnState(ctx.diffitUser).state === 'recent',
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.PREMIUM_ENDED_INVITE,
        emoji: '⏳',
        headline: 'Your school subscription has ended',
        body: 'Get 30 more days when you share Diffit with colleagues.',
        modal: shareForPremiumModal('sidebar_premium_ended_invite_ad'),
      },
      resource: {
        id: AD_IDS.PREMIUM_ENDED_INVITE,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your school subscription has ended.',
        body: 'Get 30 more days of premium when you share Diffit with colleagues.',
        modal: shareForPremiumModal('resource_premium_ended_invite_ad'),
      },
    },
  },
  {
    // The same ask for a teacher whose school never subscribed: their own trial is the clock
    // running out, so there is nothing to "renew" — the admin is being asked to buy.
    //
    // This one DOES promise the 30 days, where the school-licence warning above cannot: the
    // promo code refuses a paid user, and a trial teacher is not one — redeeming simply pushes
    // their trial_end_date out. `offersExtensionDays` is still what gates the promise, so a
    // teacher who has already spent the code is never offered it twice.
    name: 'trialExpiring',
    ctaLabel: BUY_CTA,
    eligible: (ctx) =>
      ctx.canAskAdminToRenew &&
      ctx.offersExtensionDays &&
      isTrialExpiringSoon(ctx.diffitUser, ctx.expiringSoonWindowDays),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.TRIAL_EXPIRING,
        emoji: '⏳',
        headline: 'Your premium trial ends {days}',
        body: 'You can continue using the free version of Diffit, or extend your trial by sharing Diffit with your admin.',
        modal: renewModal('sidebar_trial_expiring_ad'),
      },
      resource: {
        id: AD_IDS.TRIAL_EXPIRING,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your premium trial ends {days}.',
        body: 'Extend your trial by sharing Diffit with your admin.',
        modal: renewModal('resource_trial_expiring_ad'),
      },
    },
  },
  {
    // trialExpiring for a school too small for the admin route — see premiumEndedInvite above.
    name: 'trialExpiringInvite',
    ctaLabel: INVITE_CTA,
    eligible: (ctx) =>
      ctx.canAskColleaguesToShare &&
      ctx.offersExtensionDays &&
      isTrialExpiringSoon(ctx.diffitUser, ctx.expiringSoonWindowDays),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.TRIAL_EXPIRING_INVITE,
        emoji: '⏳',
        headline: 'Your premium trial ends {days}',
        body: 'You can continue using the free version of Diffit, or extend your trial by sharing Diffit with colleagues.',
        modal: shareForPremiumModal('sidebar_trial_expiring_invite_ad'),
      },
      resource: {
        id: AD_IDS.TRIAL_EXPIRING_INVITE,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your premium trial ends {days}.',
        body: 'Extend your trial by sharing Diffit with colleagues.',
        modal: shareForPremiumModal('resource_trial_expiring_invite_ad'),
      },
    },
  },
  {
    // The lapsed half of trialExpiring. Promises the days like its expiring twin and for the
    // same reason — an ended trial leaves them unpaid, so the code applies and simply restarts
    // the clock (increase_trial_end_date takes a past end date from utcnow, not from it).
    name: 'trialEnded',
    ctaLabel: BUY_CTA,
    eligible: (ctx) =>
      ctx.canAskAdminToRenew && ctx.offersExtensionDays && isTrialRecentlyEnded(ctx.diffitUser),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.TRIAL_ENDED,
        emoji: '⏳',
        headline: 'Your premium trial has ended',
        body: 'You can continue using the free version of Diffit, or extend your trial by sharing Diffit with your admin.',
        modal: renewModal('sidebar_trial_ended_ad'),
      },
      resource: {
        id: AD_IDS.TRIAL_ENDED,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your premium trial has ended.',
        body: 'You can continue using the free version of Diffit, or extend your trial by sharing Diffit with your admin.',
        modal: renewModal('resource_trial_ended_ad'),
      },
    },
  },
  {
    // The share route for the same state, for a school too small for the admin one.
    name: 'trialEndedInvite',
    ctaLabel: INVITE_CTA,
    eligible: (ctx) =>
      ctx.canAskColleaguesToShare &&
      ctx.offersExtensionDays &&
      isTrialRecentlyEnded(ctx.diffitUser),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.TRIAL_ENDED_INVITE,
        emoji: '⏳',
        headline: 'Your premium trial has ended',
        body: 'You can continue using the free version of Diffit, or extend your trial by sharing Diffit with colleagues.',
        modal: shareForPremiumModal('sidebar_trial_ended_invite_ad'),
      },
      resource: {
        id: AD_IDS.TRIAL_ENDED_INVITE,
        kind: 'promo',
        icon: ClockIcon,
        headline: 'Your premium trial has ended.',
        body: 'You can continue using the free version of Diffit, or extend your trial by sharing Diffit with colleagues.',
        modal: shareForPremiumModal('resource_trial_ended_invite_ad'),
      },
    },
  },
  {
    // Below the expiry cards, above every promotion: a district that has just approved the
    // extension is news for that school, where the promotions below are general. The flag
    // is checked first so only the targeted domains pay for the extension ping.
    name: 'chromeExtension',
    ctaLabel: 'Add to Chrome',
    href: CHROME_EXTENSION_URL,
    eligible: async (ctx) => ctx.isChromeExtensionAudience && !(await isExtensionInstalled()),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.CHROME_EXTENSION,
        emoji: '🧩',
        external: true,
        headline: 'Try the Chrome Extension',
        body: 'No new tab. No copy paste. The Diffit extension meets you where you plan.',
      },
    },
  },
  {
    // District news, so above the general promotions. Admin first: an admin who also teaches
    // gets this one.
    name: 'curriculumAnnouncementAdmin',
    firstSeenKey: ADMIN_FIRST_SEEN_KEY,
    retireOnClick: true,
    learnMoreHref: CURRICULUM_LEARN_MORE_URL,
    href: adminShareMailto,
    copyText: adminShareMessage,
    eligible: (ctx) =>
      !!ctx.announcedCurriculumId &&
      isOrgAdmin(ctx.diffitUser) &&
      isWithinAnnouncementWindow(ctx.diffitUser?.seen_nux_json, ADMIN_FIRST_SEEN_KEY),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.CURRICULUM_ANNOUNCEMENT_ADMIN,
        emoji: '📐',
        headline: (ctx) => `Your teachers now have access to ${curriculumName(ctx)}`,
        body: "They can align Diffit resources to the lessons they're teaching.",
        ctaLabel: 'Copy message',
        emailLabel: 'Email your teachers',
      },
    },
  },
  {
    // Free teachers are left out: curriculum alignment is premium, so the CTA would upsell them.
    name: 'curriculumAnnouncement',
    firstSeenKey: TEACHER_FIRST_SEEN_KEY,
    learnMoreHref: CURRICULUM_LEARN_MORE_URL,
    href: curriculumLessonHref,
    eligible: isCurriculumAnnouncementTeacher,
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.CURRICULUM_ANNOUNCEMENT,
        emoji: '📐',
        headline: (ctx) => `Your district turned on ${curriculumName(ctx)}`,
        body: "Align your Diffit resources to the lessons you're teaching.",
        ctaLabel: 'Align a resource',
      },
      resource: {
        id: AD_IDS.CURRICULUM_ANNOUNCEMENT,
        kind: 'promo',
        icon: AcademicCapIcon,
        headline: (ctx) => `Your district turned on ${curriculumName(ctx)}.`,
        body: "Align your next resource to the lesson you're teaching.",
        ctaLabel: 'Try it',
        eligible: (ctx) => ctx.isMathResource && isCurriculumAnnouncementTeacher(ctx),
      },
    },
  },
  {
    name: 'donuts',
    ctaLabel: 'Host a session',
    href: '/donuts-with-diffit',
    eligible: async (ctx) =>
      isDonutsAudience(ctx.diffitUser) &&
      ctx.countryCode === 'US' &&
      (await isDonutsHostEligible()),
    surfaces: {
      sidebar: { id: SIDEBAR_AD_IDS.DONUTS, emoji: '🍩', ...DONUTS_COPY },
      export: { id: EXPORT_AD_IDS.DONUTS, emoji: '🍩', ...DONUTS_COPY },
      resource: {
        id: AD_IDS.DONUTS,
        kind: 'promo',
        icon: DoughnutIcon,
        headline: 'Share Diffit in a PD session.',
        body: 'Materials and snacks on us!',
      },
    },
  },
  {
    name: 'mathChallenge',
    ctaLabel: 'Learn more',
    href: 'https://web.diffit.me/bts-2026-math-challenge',
    eligible: (ctx) => isMathStarTeacher(ctx.diffitUser),
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.MATH_CHALLENGE,
        emoji: '🎁',
        external: true,
        ...MATH_CHALLENGE_COPY,
      },
      export: { id: EXPORT_AD_IDS.MATH_CHALLENGE, emoji: '🎁', ...MATH_CHALLENGE_COPY },
      resource: {
        id: AD_IDS.MATH_CHALLENGE,
        kind: 'promo',
        icon: GiftIcon,
        // Says "other teachers" where the card surfaces say "other math teachers".
        headline: MATH_CHALLENGE_COPY.headline,
        body: 'Get rewards for inspiring other teachers.',
      },
    },
  },
  {
    name: 'mathAwareness',
    eligible: (ctx) => isMathCapable(ctx.diffitUser) && ctx.tier === 'paid',
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.MATH_AWARENESS,
        image: mathCapabilitiesArt,
        imageAlt: '',
        headline: 'Now with graphs, equations & diagrams',
        body: 'Try our enhanced support for math instruction.',
        ctaLabel: 'Create a math lesson',
        prompt: MATH_PROMPT,
      },
      export: {
        id: EXPORT_AD_IDS.MATH_AWARENESS,
        headline: 'Try our enhanced support for Math',
        body: 'Diffit now supports graphs, equations and diagrams for 99%+ of math standards.',
        prompts: MATH_PROMPTS,
      },
    },
  },
  {
    name: 'mathShare',
    ctaLabel: 'Gift a free year',
    eligible: (ctx) =>
      isMathCapable(ctx.diffitUser) && ctx.hasClaimedMathOffer && ctx.isMathShareCampaignLive,
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.MATH_SHARE,
        emoji: '💚',
        ...MATH_SHARE_COPY,
        modal: mathShareModal({
          ariaLabel: 'Share a free year of Diffit Premium',
          subhead: MATH_SHARE_COPY.body,
          inviteSource: INVITE_SOURCE.SIDEBAR_AD,
          trackingPrefix: 'Sidebar Math Share Ad',
        }),
      },
      export: {
        id: EXPORT_AD_IDS.MATH_SHARE,
        emoji: '💚',
        ...MATH_SHARE_COPY,
        modal: mathShareModal({
          ariaLabel: 'Share a free year of Diffit Premium',
          subhead: MATH_SHARE_COPY.body,
          inviteSource: INVITE_SOURCE.EXPORT_AD,
          trackingPrefix: 'Export Math Share Ad',
        }),
      },
    },
  },
  {
    name: 'mathFreeYear',
    eligible: (ctx) =>
      isMathCapable(ctx.diffitUser) &&
      (ctx.tier === 'free' || ctx.tier === 'trial') &&
      !ctx.hasClaimedMathOffer,
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.MATH_FREE_YEAR,
        image: mathCapabilitiesArt,
        imageAlt: '',
        ...MATH_FREE_YEAR_COPY,
        ctaLabel: 'Create a math lesson',
        prompt: MATH_PROMPT,
      },
      export: {
        id: EXPORT_AD_IDS.MATH_FREE_YEAR,
        ...MATH_FREE_YEAR_COPY,
        prompts: MATH_PROMPTS,
      },
      resource: {
        id: AD_IDS.MATH_FREE_YEAR,
        kind: 'math-free-year',
        eligible: (ctx) =>
          isMathCapable(ctx.diffitUser) &&
          (ctx.tier === 'free' || ctx.tier === 'trial') &&
          ctx.isMathResource &&
          (!ctx.diffitUser?.seen_nux_json?.math_promo_claimed || ctx.claimedThisSession),
      },
    },
  },
  {
    name: 'referral',
    // Headline, body and CTA come from the program's promo copy; the text below is the default.
    programCopy: true,
    ctaLabel: 'Start sharing',
    eligible: (ctx) => ctx.isReferralCampaignLive,
    surfaces: {
      sidebar: {
        id: SIDEBAR_AD_IDS.REFERRAL,
        emoji: '💚',
        ...REFERRAL_COPY,
        modal: shareReferralModal({
          inviteProgramKey: AD_REFERRAL_PROGRAM_KEY,
          inviteSource: INVITE_SOURCE.SIDEBAR_AD,
          trackingPrefix: 'Sidebar Referral Ad',
        }),
      },
      export: {
        id: EXPORT_AD_IDS.REFERRAL,
        emoji: '💚',
        ...REFERRAL_COPY,
        modal: shareReferralModal({
          inviteProgramKey: AD_REFERRAL_PROGRAM_KEY,
          inviteSource: INVITE_SOURCE.EXPORT_AD,
          trackingPrefix: 'Export Referral Ad',
        }),
      },
      resource: {
        id: AD_IDS.REFERRAL,
        kind: 'promo',
        icon: HeartIcon,
        headline: 'Love Diffit? Share Diffit.',
        body: 'Earn rewards for every teacher you bring to Diffit.',
        ctaLabel: 'Start Sharing',
        modal: shareReferralModal({
          inviteProgramKey: AD_REFERRAL_PROGRAM_KEY,
          inviteSource: INVITE_SOURCE.IN_RESOURCE_AD,
          trackingPrefix: 'In-App Referral Ad',
        }),
      },
    },
  },
];

/**
 * The descriptor list for one surface, in priority order.
 *
 * Exported for tests; product code should take the prebuilt `AD_REGISTRIES`
 * below so every consumer shares one referentially stable array.
 */
export const registryFor = (surface) =>
  CAMPAIGNS.filter((campaign) => campaign.surfaces[surface]).map(
    ({ surfaces, name, ...shared }) => {
      const descriptor = { ...shared, ...surfaces[surface] };
      // A surface opts out of an inherited field by naming it `undefined`; drop
      // those so a card's `ad.ctaLabel ? …` test sees an absent key, not a
      // present one holding undefined.
      Object.keys(descriptor).forEach((key) => {
        if (descriptor[key] === undefined) delete descriptor[key];
      });
      return descriptor;
    }
  );

export const AD_REGISTRIES = {
  sidebar: registryFor('sidebar'),
  export: registryFor('export'),
  resource: registryFor('resource'),
};
